import { CRM_PERMISSION as CASINO_CRM_PERMISSIONS } from '../casino/pages/Helpers/CrmPermissions';

import { IS_CASINO_ENABLED } from './Constants';

/**
 * Allows to recognize the property type as a string literal type.
 */
function set<T extends string>(value: T) {
    return value;
}

/**
 * User Permissions
 */
export const PERMISSION = {
    VIEW_USER: set('view user'),
    CREATE_USER: set('create user'),
    UPDATE_USER: set('update user'),
    DELETE_USER: set('delete user'),

    DOWNLOAD_USERS: set('download users'),
    DOWNLOAD_DEPOSITS: set('download deposits'),
    DOWNLOAD_TICKETS: set('download tickets'),
    DOWNLOAD_WITHDRAWALS: set('download withdrawals'),
    DOWNLOAD_RESELLERS: set('download resellers'),
    DOWNLOAD_BETS: set('download bets'),

    VIEW_RESELLER: set('view reseller'),
    CREATE_RESELLER: set('create reseller'),
    UPDATE_RESELLER: set('update reseller'),

    VIEW_RESELLER_APPLICANT: set('view reseller applicant'),
    UPDATE_RESELLER_APPLICANT: set('update reseller applicant'),

    VIEW_LOTTERY: set('view lottery'),

    VIEW_PROMO_CODE: set('view promo code'),
    CREATE_PROMO_CODE: set('create promo code'),
    UPDATE_PROMO_CODE: set('update promo code'),
    DELETE_PROMO_CODE: set('delete promo code'),

    VIEW_AFFILIATE: set('view affiliate'),
    CREATE_AFFILIATE: set('create affiliate'),
    UPDATE_AFFILIATE: set('update affiliate'),
    DELETE_AFFILIATE: set('delete affiliate'),

    VIEW_SUB_AFFILIATE: set('view sub_affiliate'),
    CREATE_SUB_AFFILIATE: set('create sub_affiliate'),
    UPDATE_SUB_AFFILIATE: set('update sub_affiliate'),
    DELETE_SUB_AFFILIATE: set('delete sub_affiliate'),

    VIEW_WITHDRAWAL: set('view withdrawal'),
    UPDATE_WITHDRAWAL: set('update withdrawal'),

    VIEW_DEPOSIT: set('view deposit'),

    VIEW_DEPOSIT_METHOD: set('view deposit_method'),

    VIEW_WITHDRAWAL_METHOD: set('view withdrawal_method'),
    CREATE_WITHDRAWAL_METHOD: set('create withdrawal_method'),
    UPDATE_WITHDRAWAL_METHOD: set('update withdrawal_method'),
    DELETE_WITHDRAWAL_METHOD: set('delete withdrawal_method'),

    VIEW_ARTICLE: set('view article'),
    CREATE_ARTICLE: set('create article'),
    UPDATE_ARTICLE: set('update article'),
    DELETE_ARTICLE: set('delete article'),

    VIEW_FAQ: set('view faq'),
    CREATE_FAQ: set('create faq'),
    UPDATE_FAQ: set('update faq'),
    DELETE_FAQ: set('delete faq'),

    VIEW_MENU: set('view menu'),
    CREATE_MENU: set('create menu'),
    UPDATE_MENU: set('update menu'),
    DELETE_MENU: set('delete menu'),

    VIEW_MENU_ITEM: set('view menu_item'),
    CREATE_MENU_ITEM: set('create menu_item'),
    UPDATE_MENU_ITEM: set('update menu_item'),
    DELETE_MENU_ITEM: set('delete menu_item'),

    VIEW_ANY_BLOCKED_COUNTRY: set('viewAny blocked_country'),
    VIEW_BLOCKED_COUNTRY: set('view blocked_country'),
    CREATE_BLOCKED_COUNTRY: set('create blocked_country'),
    DELETE_BLOCKED_COUNTRY: set('delete blocked_country'),

    VIEW_SLIDER: set('view slider'),

    VIEW_SLIDER_ITEM: set('view slider_item'),
    CREATE_SLIDER_ITEM: set('create slider_item'),
    UPDATE_SLIDER_ITEM: set('update slider_item'),
    DELETE_SLIDER_ITEM: set('delete slider_item'),

    VIEW_ANY_ADMIN: set('viewAny admin'),
    VIEW_ADMIN: set('view admin'),
    CREATE_ADMIN: set('create admin'),
    UPDATE_ADMIN: set('update admin'),
    DELETE_ADMIN: set('delete admin'),

    VIEW_CUSTOM_BOX: set('view custom_box'),
    UPDATE_CUSTOM_BOX: set('update custom_box'),

    VIEW_NEWS: set('view news'),
    CREATE_NEWS: set('create news'),
    UPDATE_NEWS: set('update news'),
    DELETE_NEWS: set('delete news'),

    VIEW_PAYMENT_METHOD: set('view payment_method'),
    CREATE_PAYMENT_METHOD: set('create payment_method'),
    UPDATE_PAYMENT_METHOD: set('update payment_method'),
    DELETE_PAYMENT_METHOD: set('delete payment_method'),

    VIEW_ANY_PAYMENT_METHOD_CHANNEL: set('viewAny payment_method_channel'),
    VIEW_PAYMENT_METHOD_CHANNEL: set('view payment_method_channel'),
    CREATE_PAYMENT_METHOD_CHANNEL: set('create payment_method_channel'),
    UPDATE_PAYMENT_METHOD_CHANNEL: set('update payment_method_channel'),
    DELETE_PAYMENT_METHOD_CHANNEL: set('delete payment_method_channel'),

    CREATE_MANUAL_DEPOSIT: set('create manual deposit'),
    CREATE_MANUAL_WITHDRAWAL: set('create manual withdrawal'),

    VIEW_MANUAL_NOTIFICATION: set('view manual_notification'),
    CREATE_MANUAL_NOTIFICATION: set('create manual_notification'),
    UPDATE_MANUAL_NOTIFICATION: set('update manual_notification'),
    DELETE_MANUAL_NOTIFICATION: set('delete manual_notification'),

    VIEW_DATABASE_LOGS: set('view database_logs'),

    VIEW_ROLE: set('view role'),
    UPDATE_ROLE: set('update role'),

    VIEW_PERMISSION: set('view permission'),

    VIEW_METRICS: set('view metrics'),

    VIEW_VOUCHER: set('view voucher'),

    VIEW_SEO_TAG: set('view seotag'),
    CREATE_SEO_TAG: set('create seotag'),
    UPDATE_SEO_TAG: set('update seotag'),
    DELETE_SEO_TAG: set('delete seotag'),

    VIEW_FILE_MANAGER: set('view file_manager'),

    VIEW_POSTBACK: set('view postback'),
    CREATE_POSTBACK: set('create postback'),
    UPDATE_POSTBACK: set('update postback'),
    DELETE_POSTBACK: set('delete postback'),

    VIEW_TICKET: set('view ticket'),
    VIEW_SCRATCHCARDS_TICKET: set('view scratchcard_ticket'),

    VIEW_LOOTBOX: set('viewAny lootbox'),

    VIEW_PAGE_SETTINGS: set('view page-settings'),
    PAGE_SETTINGS_UPDATE: set('update page-settings'),

    VIEW_DRAW: set('view draw'),

    CREATE_MASTER_AFFILIATE_AGENT: set('create master affiliate agent'),
    VIEW_MASTER_AFFILIATE_AGENT: set('view master_affiliate_agent'),
    UPDATE_MASTER_AFFILIATE_AGENT: set('update master_affiliate_agent'),
    DELETE_MASTER_AFFILIATE_AGENT: set('delete master_affiliate_agent'),

    VIEW_FAQ_CATEGORY: set('view faq_category'),
    CREATE_FAQ_CATEGORY: set('create faq_category'),
    UPDATE_FAQ_CATEGORY: set('update faq_category'),
    DELETE_FAQ_CATEGORY: set('delete faq_category'),

    VIEW_CUSTOM_REDIRECT: set('view custom_redirects'),
    CREATE_CUSTOM_REDIRECT: set('create custom_redirects'),
    UPDATE_CUSTOM_REDIRECT: set('update custom_redirects'),
    DELETE_CUSTOM_REDIRECT: set('delete custom_redirects'),

    // Casino permissions
    ...(IS_CASINO_ENABLED ? CASINO_CRM_PERMISSIONS : {}),
};

/**
 * User Roles
 */
export const ROLE = {
    AFFILIATE_OWNER: set('Affiliate Owner'),
    SUPER_ADMIN: set('super-admin'),
    ADMIN: set('Admin'),
    WHITELABEL_SUPER_ADMIN: set('Whitelabel Super Admin'),
    WHITELABEL_ADMIN: set('Whitelabel Admin'),
    SUPER_AGENT: set('Super Agent'),
    MASTER_AGENT: set('Master Agent'),
    MASTER_AFFILIATE_AGENT: set('Master Affiliate Agent'),
    MODERATOR: set('Moderator'),
    OBSERVER: set('Observer'),
};

/**
 * A string union containing the values of all predefined constants.
 */
export type PermissionType = typeof PERMISSION[keyof typeof PERMISSION];
export type RoleType = typeof ROLE[keyof typeof ROLE];

/**
 * An array type that only accepts permission values.
 */
export type PermissionList = Array<PermissionType>;

/**
 * An array type that only accepts role values.
 */
export type RoleList = Array<RoleType>;

export type OptionalRolesOrPermissions =
    | { roles?: RoleList; permissions: PermissionList }
    | { roles: RoleList; permissions?: PermissionList };
